import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  alpha,
  Link as MuiLink,
  Avatar,
  Chip,
  Fade,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Home as HomeIcon,
  LocalMall as LocalMallIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { FormulationTabs } from './formulations-components';

// Styled components matching invoice page patterns
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  switch (status) {
    case 'approved':
      color = 'success';
      break;
    case 'pending':
      color = 'warning';
      break;
    case 'rejected':
      color = 'error';
      break;
    case 'draft':
      color = 'default';
      break;
    case 'testing':
      color = 'info';
      break;
    default:
      color = 'default';
  }
  return {
    fontWeight: 500,
    borderRadius: 8,
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    minWidth: 80,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    backgroundColor: color === 'default' ? theme.palette.grey[100] : alpha(theme.palette[color].main, 0.1),
    color: color === 'default' ? theme.palette.text.primary : theme.palette[color].main,
  };
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    transition: 'all 0.2s ease'
  },
  cursor: 'pointer',
  transition: 'all 0.2s ease'
}));

const FormulationsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel, canCreateFormulations } = usePermissions();
  const theme = useTheme();
  const [companyName, setCompanyName] = useState('');

  const [formulations, setFormulations] = useState([]);
  const [filteredFormulations, setFilteredFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state matching InvoicesPage
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // View mode state
  const [viewMode, setViewMode] = useState('list');
  const [currentFormulation, setCurrentFormulation] = useState(null);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [phases, setPhases] = useState([]);
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [user, setUser] = useState(null);
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFormulation, setSelectedFormulation] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Create a ref for FormulationTabs component
  const formulaTabsRef = useRef({ openEditDialog: () => {} });

  // Check permissions using canAccessPage like other pages
  const canView = canAccessPage('formulations');
  const canEdit = canEditPage('formulations');
  const canPermanentlyDelete = hasPermission('products.formulations.delete_permanent', 'write');

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchFormulations = async () => {
    try {
      const { data, error } = await supabase
        .from('formulations')
        .select(`
          *,
          formulation_costs(
            total_batch_cost,
            cost_per_unit,
            calculated_at
          )
        `)
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      
      setFormulations(data || []);
      setFilteredFormulations(data || []);
    } catch (err) {
      console.error('Error fetching formulations:', err);
      throw err;
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('raw_materials')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setRawMaterials(data || []);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchFormulations(),
        fetchProducts(),
        fetchRawMaterials(),
        fetchUser(),
        fetchCompanyData()
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormulationDetails = async (formulationId) => {
    try {
      setLoading(true);
      
      // Fetch the formulation details
      const { data: formulationData, error: formulationError } = await supabase
        .from('formulations')
        .select('*')
        .eq('id', formulationId)
        .single();
      
      if (formulationError) throw formulationError;
      
      setCurrentFormulation(formulationData);
      
      // Fetch formulation phases
      const { data: phasesData, error: phasesError } = await supabase
        .from('formulation_phases')
        .select('*')
        .eq('formulation_id', formulationId)
        .order('sequence');
      
      if (phasesError) throw phasesError;
      
      setPhases(phasesData || []);
      
      // Fetch formulation ingredients
      const { data: ingredientsData, error: ingredientsError } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          id,
          formulation_id,
          formulation_phase_id,
          ingredient_id,
          percentage,
          amount,
          amount_unit,
          sequence,
          function,
          notes,
          supplier_link_id,
          created_at,
          updated_at,
          raw_materials:ingredient_id (id, name, cas_number, description, sku, category, material_type, primary_unit_measure, color, form)
        `)
        .eq('formulation_id', formulationId)
        .order('sequence');
      
      if (ingredientsError) throw ingredientsError;
      
      setFormulationIngredients(ingredientsData || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching formulation details:', error);
      setError('Error loading formulation details: ' + error.message);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...formulations];
    
    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(formulation => 
        (formulation.name && formulation.name.toLowerCase().includes(lowercasedSearch)) ||
        (formulation.type && formulation.type.toLowerCase().includes(lowercasedSearch)) ||
        (formulation.description && formulation.description.toLowerCase().includes(lowercasedSearch))
      );
    }
    
    setFilteredFormulations(result);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, formulation) => {
    setSelectedFormulation(formulation);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setCurrentFormulation(null);
    setPhases([]);
    setFormulationIngredients([]);
  };

  const handleDeleteFormulation = async () => {
    if (!selectedFormulation) return;
    
    // Check permissions before proceeding - require permanent delete permission
    if (!canPermanentlyDelete) {
      setError('You do not have permission to permanently delete formulations.');
      handleCloseDeleteDialog();
      return;
    }
    
    setDeleting(true);
    try {
      // Check if formulation is being used in production batches
      const { data: batchesUsingFormulation, error: batchCheckError } = await supabase
        .from('production_batches')
        .select('id, batch_number, status')
        .eq('formulation_id', selectedFormulation.id)
        .limit(1);
      
      if (batchCheckError) throw batchCheckError;
      
      if (batchesUsingFormulation && batchesUsingFormulation.length > 0) {
        setError(`Cannot delete formulation "${selectedFormulation.name}" because it is being used in batch production logs. Formulations used in production batches cannot be deleted to maintain data integrity and traceability.`);
        handleCloseDeleteDialog();
        return;
      }
      
      const { error } = await supabase
        .from('formulations')
        .delete()
        .eq('id', selectedFormulation.id);
      
      if (error) throw error;
      
      // Remove from both formulations and filteredFormulations state
      const updatedFormulations = formulations.filter(f => f.id !== selectedFormulation.id);
      setFormulations(updatedFormulations);
      setFilteredFormulations(filteredFormulations.filter(f => f.id !== selectedFormulation.id));
      
      // If we deleted the currently viewed formulation, go back to list
      if (currentFormulation && currentFormulation.id === selectedFormulation.id) {
        handleBackToList();
      }
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting formulation:', err);
      setError(`Failed to delete formulation: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditFormulation = () => {
    if (!selectedFormulation) return;
    handleViewDetails(selectedFormulation);
    handleCloseMenu();
  };

  const handleViewDetails = async (formulation) => {
    setViewMode('detail');
    await fetchFormulationDetails(formulation.id);
  };

  const handleCreateNew = () => {
    const newFormulation = {
      name: '',
      code: '',
      description: '',
      product_id: '',
      target_batch_size: 100,
      unit_of_measure: 'g',
      version: '1.0',
      status: 'draft',
      type: '',
      notes: '',
      company_id: companyId,
      ph_target: '',
      viscosity_target: '',
      appearance: '',
      yield_percentage: ''
    };
    
    setCurrentFormulation(newFormulation);
    setViewMode('detail');
    
    // Open edit dialog after a short delay
    setTimeout(() => {
      if (formulaTabsRef.current) {
        formulaTabsRef.current.openEditDialog(true);
      }
    }, 100);
  };

  const refreshAllData = async (formulationId = null) => {
    try {
      await fetchFormulations();
      
      if (formulationId) {
        await fetchFormulationDetails(formulationId);
      } else if (currentFormulation && currentFormulation.id) {
        await fetchFormulationDetails(currentFormulation.id);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    // You can implement a snackbar or use the existing alert system
    if (severity === 'error') {
      setError(message);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [companyId]);

  useEffect(() => {
    if (formulations.length > 0) {
      applyFilters();
    }
  }, [formulations, searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, rowsPerPage]);

  // Permission check
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Formulations Lab Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Formulations Lab. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // List view render
  const renderFormulationsList = () => (
    <Container 
      maxWidth="xl"
      sx={{
        py: 3,
        pb: 8,
        px: { xs: 2, sm: 3 },
        width: '100%',
        maxWidth: '100vw !important'
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{ 
          borderRadius: 3,
          mb: 3,
          mt: 5,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          borderTop: '3px solid #E91E63',
          boxShadow: 'none',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#E91E63', 0.08),
              }}>
                <ScienceIcon sx={{ fontSize: 32, color: '#E91E63' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#E91E63',
                  mb: 1,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Formulation Lab
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  {companyName || 'Loading...'}
                </Typography>
              </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                startIcon={<RefreshIcon />}
                onClick={fetchFormulations}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'grey.400',
                  color: 'primary.main',
                  borderWidth: 1,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                  }
                }}
              >
                Refresh
              </Button>
              {canCreateFormulations() && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    borderRadius: 2,
                    borderColor: 'grey.400',
                    color: 'primary.main',
                    borderWidth: 1,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    background: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      borderColor: 'grey.600',
                      backgroundColor: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                    }
                  }}
                >
                  New Formulation
                </Button>
              )}
            </Box>
          </Box>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Create and manage product formulations with precise ingredient tracking and cost analysis.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Bar */}
      <SectionPaper>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search formulations by name, type, or description..."
            variant="outlined"
            size="medium"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 2,
                background: 'white'
              }
            }}
          />

          <Button 
            startIcon={<RefreshIcon />}
            onClick={fetchFormulations}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              borderColor: 'grey.400',
              color: 'primary.main',
              borderWidth: 1,
              fontWeight: 500,
              px: 3,
              py: 1.5,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'white',
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
              }
            }}
          >
            Refresh
          </Button>
        </Stack>
      </SectionPaper>

      {/* Formulations Table */}
      {loading ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#E91E63' }} />
        </Box>
      ) : filteredFormulations.length === 0 ? (
        <Paper 
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 2,
            width: 100,
            height: 100,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ScienceIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {formulations.length === 0 ? 'No Formulations Yet' : 'No Matching Formulations'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            {formulations.length === 0 
              ? 'Create your first formulation to start developing your product line.'
              : 'Try adjusting your search criteria or clear filters.'}
          </Typography>
          {formulations.length === 0 && canCreateFormulations() && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{ 
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Create Your First Formulation
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Paper elevation={0} sx={{ 
            borderRadius: 3, 
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
             <TableContainer sx={{ 
               maxHeight: 'none',
               '&::-webkit-scrollbar': {
                 display: 'none'
               },
               '-ms-overflow-style': 'none',
               'scrollbar-width': 'none'
             }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Formulation
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Batch Size
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Batch Cost
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3,
                        width: 160
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? filteredFormulations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : filteredFormulations
                  ).map((formulation, index) => (
                    <TableRow 
                      key={formulation.id} 
                      hover
                      onClick={() => handleViewDetails(formulation)}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          transition: 'all 0.2s ease',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2.5,
                          px: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{ 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              mr: 2,
                              width: 40,
                              height: 40,
                              fontSize: '1rem',
                              fontWeight: 500
                            }}
                          >
                            {formulation.name ? formulation.name.charAt(0).toUpperCase() : 'F'}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.95rem'
                              }}
                            >
                              {formulation.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {formulation.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary',
                            fontSize: '0.9rem'
                          }}
                        >
                          {formulation.type ? formulation.type.charAt(0).toUpperCase() + formulation.type.slice(1) : 'Unspecified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary',
                            fontSize: '0.9rem'
                          }}
                        >
                          {formulation.target_batch_size ? `${formulation.target_batch_size} ${formulation.unit_of_measure || 'g'}` : 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary',
                            fontSize: '0.9rem',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formulation.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip 
                          status={formulation.status}
                          label={formulation.status ? formulation.status.charAt(0).toUpperCase() + formulation.status.slice(1) : 'Draft'}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                            borderRadius: 2
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {formulation.costs_complete && formulation.formulation_costs && formulation.formulation_costs.length > 0 && formulation.formulation_costs[0].total_batch_cost ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 500, 
                              color: 'success.main',
                              fontSize: '0.95rem'
                            }}>
                              ${parseFloat(formulation.formulation_costs[0].total_batch_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {formulation.target_batch_size} {formulation.unit_of_measure || 'g'} batch
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={(canEdit || canPermanentlyDelete) ? "More options" : "No actions available"}>
                          <IconButton 
                            size="small"
                            disabled={!(canEdit || canPermanentlyDelete)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMenu(e, formulation);
                            }}
                            sx={{
                              color: (canEdit || canPermanentlyDelete) ? 'text.primary' : 'text.disabled',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              borderRadius: 2,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': (canEdit || canPermanentlyDelete) ? {
                                bgcolor: 'primary.main',
                                color: 'white',
                                transform: 'scale(1.05)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                              } : {},
                              '&.Mui-disabled': {
                                color: 'text.disabled',
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderRadius: '0 0 12px 12px'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredFormulations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                },
                '& .MuiTablePagination-select': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    margin: '0 2px',
                    background: 'white',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateY(-1px)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    '&.Mui-disabled': {
                      background: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[300]}`,
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }
                }
              }}
            />
          </Box>
          </Paper>
        </>
      )}

      {/* Formulation Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            minWidth: 180
          }
        }}
      >
        {canEdit && (
          <MenuItem onClick={handleEditFormulation} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Edit Formulation</ListItemText>
          </MenuItem>
        )}
        {canPermanentlyDelete && (
          <MenuItem onClick={handleOpenDeleteDialog} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Permanently Delete Formulation</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, white)`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{
          px: 0,
          py: 0,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
            <DeleteIcon sx={{ fontSize: 28, color: 'error.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Permanently Delete Formulation
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseDeleteDialog} 
            sx={{ 
              mr: 2,
              color: theme.palette.text.secondary,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'scale(1.05)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            {selectedFormulation && (
              <Paper sx={{ 
                borderRadius: 3, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                overflow: 'hidden',
                p: 4
              }}>
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                  Are you sure you want to delete this formulation?
                </Typography>
                
                <Paper sx={{ 
                  p: 3, 
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                    {selectedFormulation.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Type: {selectedFormulation.type || 'Unspecified'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Batch Size: {selectedFormulation.target_batch_size} {selectedFormulation.unit_of_measure || 'g'}
                  </Typography>
                </Paper>
                
                <Alert severity="error" sx={{ borderRadius: 2, color: '#000' }}>
                  <strong>Critical Action:</strong> This will permanently delete the formulation and cannot be undone. All associated data including ingredients, procedures, and test results will be removed. This action requires special permissions.
                </Alert>
              </Paper>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: alpha(theme.palette.grey[50], 0.5),
          backdropFilter: 'blur(10px)'
        }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            disabled={deleting}
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="error"
            onClick={handleDeleteFormulation}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              bgcolor: 'error.main',
              boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                bgcolor: 'error.dark',
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
              }
            }}
          >
            {deleting ? 'Permanently Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  // Detail view render
  const renderFormulationDetail = () => {
    if (!currentFormulation) return null;
    
    return (
      <Container 
        maxWidth="xl"
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        </Box>
        
        <FormulationTabs 
          ref={formulaTabsRef}
          formulation={currentFormulation}
          rawMaterials={rawMaterials}
          phases={phases}
          showSnackbar={showSnackbar}
          user={user}
          products={products}
          formulationIngredients={formulationIngredients}
          userCompanyId={companyId}
          onUpdate={(formulationId) => {
            refreshAllData(formulationId || currentFormulation?.id);
          }}
          onBackToList={handleBackToList}
        />
      </Container>
    );
  };

  // Main render based on view mode
  return (
    <Fade in timeout={800} key={viewMode}>
      <Box>
        {viewMode === 'list' ? renderFormulationsList() : renderFormulationDetail()}
      </Box>
    </Fade>
  );
};

export default FormulationsPage;